/**
 * Route corridor math (2026-08-03) — pure functions behind the "On the
 * way" page. The proxy's `directions` action returns Google's encoded
 * polyline; everything after that happens here, client-side: decode,
 * then measure each candidate place's distance to the road.
 */

/** One zig-zag varint from Google's polyline encoding. */
function step(encoded, index) {
  let result = 0;
  let shift = 0;
  let b;
  do {
    b = encoded.charCodeAt(index++) - 63;
    result |= (b & 0x1f) << shift;
    shift += 5;
  } while (b >= 0x20);
  return [(result & 1) ? ~(result >> 1) : (result >> 1), index];
}

/** Google encoded polyline → [{lat, lng}]. */
export function decodePolyline(encoded) {
  const path = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < (encoded || '').length) {
    let d;
    [d, index] = step(encoded, index);
    lat += d;
    [d, index] = step(encoded, index);
    lng += d;
    path.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return path;
}

const KM_PER_DEG = 111.32;

/** Point-to-segment distance in km, equirectangular around the segment. */
function segmentKm(p, a, b) {
  const k = Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
  const px = (p.lng - a.lng) * k;
  const py = p.lat - a.lat;
  const bx = (b.lng - a.lng) * k;
  const by = b.lat - a.lat;
  const len2 = bx * bx + by * by;
  const t = len2 ? Math.max(0, Math.min(1, (px * bx + py * by) / len2)) : 0;
  const dx = px - t * bx;
  const dy = py - t * by;
  return { km: Math.sqrt(dx * dx + dy * dy) * KM_PER_DEG, t };
}

/**
 * How far off the road a point sits, and WHERE along the drive it falls
 * (fractional segment index — sorting results by `along` puts them in
 * driving order, which is how a road-tripper reads a list). Vertex-only
 * distance would miss places beside long straight segments, so this is
 * true point-to-segment.
 */
export function distanceToPathKm(point, path) {
  if (!path?.length) return { km: Infinity, along: 0 };
  if (path.length === 1) {
    const { km } = segmentKm(point, path[0], path[0]);
    return { km, along: 0 };
  }
  let best = { km: Infinity, along: 0 };
  for (let i = 0; i < path.length - 1; i++) {
    const { km, t } = segmentKm(point, path[i], path[i + 1]);
    if (km < best.km) best = { km, along: i + t };
  }
  return best;
}

/**
 * Filter candidates to the corridor. Each candidate needs {lat, lng};
 * survivors come back with {detourKm, along}, in driving order.
 */
export function inCorridor(candidates, path, widthKm) {
  return (candidates || [])
    .map((c) => {
      if (typeof c.lat !== 'number' || typeof c.lng !== 'number') return null;
      const { km, along } = distanceToPathKm(c, path);
      return km <= widthKm ? { ...c, detourKm: km, along } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.along - b.along);
}
